import React, { useEffect, useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { toast } from "react-toastify";
import { MdDelete } from "react-icons/md";
import { portalService } from "apiServices";
import { Buttons, Displays, Inputs } from "components";
import { AlertControl } from "components/Modals/Alert";
import { IMG_PATH } from "utils/constants/imgPath";
import {
  DOWNLOAD_SEAT_TEMPLATE,
  LAST_NEW_FILE,
  PARSE_SEAT_FILE,
  RENDER_SEAT_BOOKING_ERROR,
} from "utils/functions/seatBooking";

const MAP_TYPES = ["image/png", "image/jpeg"];

const SettingTab = ({ activityId, activity, data, canUpdate, onSaved }) => {
  const config = (data && data.config) || {};
  const stats = (data && data.seat_stats) || {};
  const gateProblems = (data && data.gate_problems) || [];
  const zones = (data && data.zones) || [];

  const { control, handleSubmit, reset, setValue } = useForm();
  const { fields, append, remove } = useFieldArray({ control, name: "schools" });

  const [_isBgLoading, _setIsBgLoading] = useState(false);
  const [_seatRows, _setSeatRows] = useState([]);
  const [_importErrors, _setImportErrors] = useState([]);
  const [_fileKey, _setFileKey] = useState(0);
  const [_mapKey, _setMapKey] = useState(0);
  const [_qrKey, _setQrKey] = useState(0);
  const [_areas, _setAreas] = useState([]);
  const [_zoneNames, _setZoneNames] = useState({});
  const [_presets, _setPresets] = useState([]);
  const [_presetKey, _setPresetKey] = useState("");

  useEffect(() => {
    portalService.GET_SEAT_BOOKING_PRESETS().then((res) => {
      if (res && res.status === 200 && Array.isArray(res.data)) {
        _setPresets(res.data);
        if (res.data.length) _setPresetKey(res.data[0].key);
      }
    });
  }, []);

  useEffect(() => {
    reset({
      price: config.price ?? 0,
      max_per_booking: config.max_per_booking ?? 6,
      online_quota: config.online_quota ?? 0,
      lock_minutes: config.lock_minutes ?? 30,
      account_no: config.account_no || "",
      account_name: config.account_name || "",
      schools: config.schools || [],
    });
    _setAreas(config.zone_areas || []);
    _setZoneNames(Object.fromEntries((config.zone_names || []).map((z) => [z.zone, z.name])));
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const _handleUploadMap = async (files) => {
    const file = LAST_NEW_FILE(files);
    if (!file) return;
    if (!MAP_TYPES.includes(file.type) || file.size > 8 * 1024 * 1024) {
      toast.error("รูปผังต้องเป็น PNG หรือ JPG ไม่เกิน 8 MB");
      _setMapKey((k) => k + 1);
      return;
    }
    const form = new FormData();
    form.append("image", file);
    _setIsBgLoading(true);
    const res = await portalService.POST_SEAT_BOOKING_MAP_UPLOAD(activityId, form);
    _setIsBgLoading(false);
    _setMapKey((k) => k + 1);
    if (res && res.status === 200) {
      toast.success("อัปโหลดรูปผังแล้ว");
      onSaved && onSaved();
    } else {
      toast.error(RENDER_SEAT_BOOKING_ERROR(res, "อัปโหลดไม่สำเร็จ"));
    }
  };

  // รูป QR บัญชีรับโอน (ลูกค้าเห็นในหน้าชำระเงิน) อัปโหลดแล้วบันทึกทันที แทนรูปเดิม
  const _handleUploadAccountQr = async (files) => {
    const file = LAST_NEW_FILE(files);
    if (!file) return;
    if (!MAP_TYPES.includes(file.type) || file.size > 8 * 1024 * 1024) {
      toast.error("รูป QR ต้องเป็น PNG หรือ JPG ไม่เกิน 8 MB");
      _setQrKey((k) => k + 1);
      return;
    }
    const form = new FormData();
    form.append("image", file);
    _setIsBgLoading(true);
    const res = await portalService.POST_SEAT_BOOKING_ACCOUNT_QR_UPLOAD(activityId, form);
    _setIsBgLoading(false);
    _setQrKey((k) => k + 1);
    if (res && res.status === 200) {
      toast.success("อัปโหลดรูป QR บัญชีแล้ว");
      onSaved && onSaved();
    } else {
      toast.error(RENDER_SEAT_BOOKING_ERROR(res, "อัปโหลดไม่สำเร็จ"));
    }
  };

  const _handleRemoveAccountQr = () =>
    AlertControl.show({
      title: "ลบรูป QR บัญชี",
      description: "ลบรูป QR บัญชีรับโอน? ลูกค้าจะเห็นแค่เลขบัญชีในหน้าชำระเงิน",
      btnLabel1: "ปิด",
      btnLabel2: "ลบ",
      onEvent2: async () => {
        _setIsBgLoading(true);
        const res = await portalService.DELETE_SEAT_BOOKING_ACCOUNT_QR(activityId);
        _setIsBgLoading(false);
        if (res && res.status === 200) {
          toast.success("ลบรูป QR บัญชีแล้ว");
          onSaved && onSaved();
        } else {
          toast.error(RENDER_SEAT_BOOKING_ERROR(res, "ลบไม่สำเร็จ"));
        }
      },
    });

  const _handleRemoveMap = () =>
    AlertControl.show({
      title: "ลบรูปผัง",
      description: "ลบรูปผังและกรอบโซนทั้งหมดบนรูป?",
      btnLabel1: "ยกเลิก",
      btnLabel2: "ลบ",
      onEvent2: async () => {
        _setIsBgLoading(true);
        const res = await portalService.DELETE_SEAT_BOOKING_MAP(activityId);
        _setIsBgLoading(false);
        if (res && res.status === 200) {
          toast.success("ลบรูปผังแล้ว");
          onSaved && onSaved();
        } else {
          toast.error(RENDER_SEAT_BOOKING_ERROR(res, "ลบไม่สำเร็จ"));
        }
      },
    });

  const _handleSaveAreas = async () => {
    _setIsBgLoading(true);
    const res = await portalService.PUT_SEAT_BOOKING_CONFIG(activityId, { zone_areas: _areas });
    _setIsBgLoading(false);
    if (res && res.status === 200) {
      toast.success("บันทึกกรอบโซนแล้ว");
      onSaved && onSaved();
    } else {
      toast.error(RENDER_SEAT_BOOKING_ERROR(res, "บันทึกไม่สำเร็จ"));
    }
  };

  const areasChanged = JSON.stringify(_areas) !== JSON.stringify(config.zone_areas || []);

  const _onSubmit = async (values) => {
    _setIsBgLoading(true);
    const res = await portalService.PUT_SEAT_BOOKING_CONFIG(activityId, {
      price: Number(values.price),
      max_per_booking: Number(values.max_per_booking),
      online_quota: Number(values.online_quota) || 0,
      lock_minutes: Number(values.lock_minutes),
      account_no: values.account_no,
      account_name: values.account_name,
      schools: (values.schools || []).filter((s) => s.code || s.name),
      zone_names: zones.map((zone) => ({ zone, name: (_zoneNames[zone] || "").trim() })).filter((z) => z.name),
    });
    _setIsBgLoading(false);
    if (res && res.status === 200) {
      toast.success("บันทึกการตั้งค่าแล้ว");
      onSaved && onSaved();
    } else {
      toast.error(RENDER_SEAT_BOOKING_ERROR(res, "บันทึกไม่สำเร็จ"));
      // ราคาเปลี่ยนไม่ได้หลังมีการจอง: คืนค่าในช่องเป็นราคาจริง ไม่ให้แอดมินเข้าใจว่าเปลี่ยนแล้ว
      if (res && res.data && res.data.error && res.data.error.code === "PRICE_LOCKED") {
        setValue("price", config.price ?? 0);
      }
    }
  };

  const _handleApplyPreset = () => {
    const preset = _presets.find((p) => p.key === _presetKey);
    if (!preset) return;
    AlertControl.show({
      title: `ใช้${preset.name}`,
      description: `ระบบจะสร้างที่นั่ง ${preset.total_seats} ที่ พร้อมรูปผังฮอลล์ กรอบโซน และชื่อโซนให้ (รูปผัง กรอบโซน และชื่อโซนเดิมจะถูกแทนที่) ที่นั่งที่จองแล้วไม่ถูกแตะ`,
      btnLabel1: "ยกเลิก",
      btnLabel2: "ใช้ผังนี้",
      onEvent2: async () => {
        _setIsBgLoading(true);
        const res = await portalService.POST_SEAT_BOOKING_PRESET_APPLY(activityId, { key: preset.key });
        _setIsBgLoading(false);
        if (res && res.status === 200) {
          toast.success(`ใช้${preset.name} แล้ว (สร้างใหม่ ${res.data.inserted} ที่ มีอยู่แล้ว ${res.data.existing} ที่)`);
          onSaved && onSaved();
        } else {
          toast.error(RENDER_SEAT_BOOKING_ERROR(res, "ใช้ผังไม่สำเร็จ"));
        }
      },
    });
  };

  const _handleClearSeatMap = () =>
    AlertControl.show({
      title: "ล้างผัง",
      description: `ลบที่นั่งทั้งหมด ${stats.total || 0} ที่ รูปผังฮอลล์ กรอบโซน และชื่อโซน เพื่อตั้งผังใหม่? (ทำได้เฉพาะตอนยังไม่มีการจอง)`,
      btnLabel1: "ยกเลิก",
      btnLabel2: "ล้างผัง",
      onEvent2: async () => {
        _setIsBgLoading(true);
        const res = await portalService.DELETE_SEAT_BOOKING_SEATS(activityId);
        _setIsBgLoading(false);
        if (res && res.status === 200) {
          toast.success(`ล้างผังแล้ว (ลบ ${res.data.removed_seats} ที่นั่ง)`);
          onSaved && onSaved();
        } else {
          toast.error(RENDER_SEAT_BOOKING_ERROR(res, "ล้างผังไม่สำเร็จ"));
        }
      },
    });

  const _handleSelectFile = async (files) => {
    const file = LAST_NEW_FILE(files);
    _setImportErrors([]);
    if (!file) return _setSeatRows([]);
    try {
      const rows = await PARSE_SEAT_FILE(file);
      _setSeatRows(rows);
      if (!rows.length) toast.error("ไม่พบข้อมูลที่นั่งในไฟล์ (ตรวจหัวคอลัมน์ Zone / Row / Seat No.)");
    } catch (error) {
      _setSeatRows([]);
      toast.error("อ่านไฟล์ไม่ได้");
    }
  };

  const _handleImportSeats = async () => {
    _setIsBgLoading(true);
    const res = await portalService.POST_SEAT_BOOKING_SEAT_IMPORT(activityId, { rows: _seatRows });
    _setIsBgLoading(false);
    if (res && res.status === 200) {
      toast.success(`นำเข้าแล้ว ${res.data.inserted} ที่นั่ง (มีอยู่แล้ว ${res.data.existing} ที่)`);
      _setSeatRows([]);
      _setFileKey((k) => k + 1);
      onSaved && onSaved();
    } else {
      const items = res && res.data && res.data.error && res.data.error.items;
      _setImportErrors(Array.isArray(items) ? items : []);
      toast.error(RENDER_SEAT_BOOKING_ERROR(res, "นำเข้าไม่สำเร็จ"));
    }
  };

  const hasSeatMap = (stats.total || 0) > 0 || !!config.seat_map_image;

  const zoneSummary = _seatRows.reduce((result, row) => {
    const zone = String(row.zone).toUpperCase() || "-";
    result[zone] = (result[zone] || 0) + 1;
    return result;
  }, {});

  const numberField = (name, label, hint) => (
    <div className="input_row">
      <div className="f_left_col">{label}</div>
      <div className="f_right_col">
        <Controller
          control={control}
          name={name}
          render={({ field }) => <Inputs.TextField {...field} type="number" disabled={!canUpdate} />}
        />
        {hint && <div className="hint">{hint}</div>}
      </div>
    </div>
  );

  return (
    <div>
      <Displays.BGLoading visible={_isBgLoading} />
      <div className="stat_row">
        <div className="stat_item">
          <div className="stat_label">ที่นั่งทั้งหมด</div>
          <div className="stat_value">{stats.total || 0}</div>
        </div>
        <div className="stat_item">
          <div className="stat_label">ว่าง</div>
          <div className="stat_value">{stats.free || 0}</div>
        </div>
        <div className="stat_item">
          <div className="stat_label">รอยืนยัน</div>
          <div className="stat_value">{stats.pending || 0}</div>
        </div>
        <div className="stat_item">
          <div className="stat_label">จองแล้ว</div>
          <div className="stat_value">{stats.taken || 0}</div>
        </div>
        <div className="stat_item">
          <div className="stat_label">กันไว้</div>
          <div className="stat_value">{stats.blocked || 0}</div>
        </div>
      </div>
      {/* ตาม design ไม่มีสวิตช์เปิดจองแยก: เปิดเองเมื่อกิจกรรม Active + อยู่ในช่วงลงทะเบียน + ตั้งค่าครบ */}
      {gateProblems.length > 0 ? (
        <div className="warn_box">
          ลูกค้ายังจองไม่ได้ ต้องตั้งค่าให้ครบก่อน:
          <ul>
            {gateProblems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="info_box">
          ตั้งค่าครบแล้ว ระบบเปิดรับจองเองเมื่อกิจกรรม Active และอยู่ในช่วง Start/End date register
          {activity && !activity.is_active ? " (ตอนนี้กิจกรรมยังไม่ Active)" : ""}
        </div>
      )}

      <form onSubmit={handleSubmit(_onSubmit)}>
        <div className="section_title">การจอง</div>
        <div className="hint" style={{ marginBottom: 12 }}>
          เปิด-ปิดการจองใช้ Active และ Start/End date register ในหน้าแก้ไขกิจกรรม (ที่เดียว)
        </div>
        {numberField("price", "ราคาต่อที่นั่ง (บาท)", "ราคาเดียวทุกโซน เปลี่ยนไม่ได้หลังมีการจองแล้ว")}
        {numberField("max_per_booking", "จองได้สูงสุดต่อครั้ง")}
        {numberField("online_quota", "โควตาจองออนไลน์", "0 = ไม่จำกัด นับเฉพาะการจองที่ลูกค้าทำเอง (นับตั้งแต่เลือกที่นั่ง) แอดมินจองให้ไม่นับ")}
        {numberField("lock_minutes", "เวลาล็อกที่นั่งระหว่างกรอก (นาที)")}

        <div className="section_title">บัญชีรับโอน</div>
        <div className="input_row">
          <div className="f_left_col">เลขบัญชี</div>
          <div className="f_right_col">
            <Controller
              control={control}
              name="account_no"
              render={({ field }) => <Inputs.TextField {...field} placeholder="เช่น 008-6-08860-2" disabled={!canUpdate} />}
            />
            <div className="hint">ตัวเลข 10-15 หลัก (มีขีดได้) แสดงให้ลูกค้าโอน และใช้ตรวจบัญชีผู้รับในสลิป</div>
          </div>
        </div>
        <div className="input_row">
          <div className="f_left_col">ชื่อบัญชี</div>
          <div className="f_right_col">
            <Controller
              control={control}
              name="account_name"
              render={({ field }) => <Inputs.TextField {...field} disabled={!canUpdate} />}
            />
          </div>
        </div>
        <div className="input_row">
          <div className="f_left_col">รูป QR Code บัญชี</div>
          <div className="f_right_col">
            {config.account_qr_image && (
              <img className="account_qr_preview" src={IMG_PATH + config.account_qr_image} alt="QR บัญชี" />
            )}
            {canUpdate && (
              <div className="inline_row">
                <div className="w400">
                  <Inputs.UploadImage
                    key={_qrKey}
                    theme_upload_image
                    acceptType="cover"
                    placeholder="เลือกรูป QR PNG / JPG ไม่เกิน 8 MB"
                    onChange={_handleUploadAccountQr}
                  />
                </div>
                {config.account_qr_image && (
                  <div className="w160">
                    <Buttons.BgStandard theme_red label="ลบรูป QR" onClick={_handleRemoveAccountQr} />
                  </div>
                )}
              </div>
            )}
            <div className="hint">
              {config.account_qr_image
                ? "ลูกค้าเห็นรูปนี้ในหน้าชำระเงิน อัปโหลดใหม่ = แทนรูปเดิม"
                : "ยังไม่มีรูป QR ลูกค้าจะเห็นแค่เลขบัญชี (แนะนำให้อัปโหลด ลูกค้าสแกนจ่ายได้ง่ายกว่า)"}
            </div>
          </div>
        </div>

        <div className="section_title">ชื่อโซน (แสดงให้ลูกค้า)</div>
        <div className="hint">ไม่บังคับ เช่น B1 = ซ้ายหน้า ลูกค้าจะเห็น "B1 ซ้ายหน้า" บนปุ่มเลือกโซน · โซนมาจากผังที่นั่งที่นำเข้า</div>
        {zones.length === 0 && <div className="hint">ยังไม่มีผังที่นั่ง</div>}
        {zones.map((zone) => (
          <div className="inline_row" key={zone}>
            <div className="w100">โซน {zone}</div>
            <div style={{ width: 300 }}>
              <Inputs.TextField
                value={_zoneNames[zone] || ""}
                placeholder="เช่น ซ้ายหน้า"
                maxLength={50}
                disabled={!canUpdate}
                onChange={(e) => _setZoneNames((prev) => ({ ...prev, [zone]: e.target.value }))}
              />
            </div>
          </div>
        ))}

        <div className="section_title">โรงเรียน (ไม่บังคับ)</div>
        <div className="hint">ลูกค้าเลือกโรงเรียนหรือ "ไม่ระบุ" ก็ได้ · ห้ามใช้รหัส GEN และ GUEST (ระบบใช้ขึ้นต้นรหัสจอง)</div>
        {fields.map((item, index) => (
          <div className="inline_row" key={item.id}>
            <div className="w140">
              <Controller
                control={control}
                name={`schools.${index}.code`}
                render={({ field }) => <Inputs.TextField {...field} placeholder="รหัส เช่น SCH01" disabled={!canUpdate} />}
              />
            </div>
            <div style={{ width: 360 }}>
              <Controller
                control={control}
                name={`schools.${index}.name`}
                render={({ field }) => <Inputs.TextField {...field} placeholder="ชื่อโรงเรียน" disabled={!canUpdate} />}
              />
            </div>
            {canUpdate && <MdDelete size={22} style={{ cursor: "pointer" }} onClick={() => remove(index)} />}
          </div>
        ))}
        {canUpdate && (
          <div className="w160" style={{ marginTop: 8 }}>
            <Buttons.BgStandard label="+ เพิ่มโรงเรียน" onClick={() => append({ code: "", name: "" })} />
          </div>
        )}
        {canUpdate && (
          <div className="inline_row" style={{ justifyContent: "flex-end", marginTop: 16 }}>
            <div className="w120">
              <Buttons.BgStandard type="submit" theme_purple label="บันทึก" />
            </div>
          </div>
        )}
      </form>

      <div className="section_title">ผังที่นั่ง</div>
      {canUpdate && (_presets.length > 0 || hasSeatMap) && (
        <>
          {_presets.length > 0 && (
            <div className="hint">
              ใช้ผังสำเร็จรูป: ได้ที่นั่ง รูปผังฮอลล์ กรอบโซน และชื่อโซนครบในครั้งเดียว ปรับต่อด้านล่างได้
            </div>
          )}
          <div className="inline_row" style={{ marginTop: 10 }}>
            {_presets.length > 0 && (
              <>
                <div className="w400">
                  <Inputs.Dropdown
                    value={_presetKey}
                    options={_presets.map((p) => ({ value: p.key, label: `${p.name} (${p.total_seats} ที่นั่ง)` }))}
                    onChange={_setPresetKey}
                  />
                </div>
                <div className="w160">
                  <Buttons.BgStandard theme_purple label="ใช้ผังนี้" onClick={_handleApplyPreset} />
                </div>
              </>
            )}
            {hasSeatMap && (
              <div className="w160">
                <Buttons.BgStandard theme_red label="ล้างผัง" onClick={_handleClearSeatMap} />
              </div>
            )}
          </div>
          {_presets.length > 0 && <div className="preset_or">หรือสร้างผังเอง</div>}
        </>
      )}
      <div className="hint">
        นำเข้าจาก Excel (Zone / Row / Seat No. / Col) นำเข้าซ้ำได้ ที่นั่งที่มีอยู่แล้วไม่ถูกแก้สถานะ แต่ตำแหน่ง Col จะเปลี่ยนตามไฟล์ล่าสุด
        รหัสที่นั่งจะเป็น {"{Zone}-{Row}{เลข 2 หลัก}"} เช่น B1-A01 · Col ไม่บังคับ ใช้เว้นทางเดิน/เยื้องแถวให้ผังเหมือนจริง
      </div>
      <div className="inline_row" style={{ marginTop: 10 }}>
        <div className="w200">
          <Buttons.BgStandard
            theme_export_btn
            label="Download Template"
            disabled={!_presets.length}
            onClick={() => _presets.length && DOWNLOAD_SEAT_TEMPLATE(_presets[0])}
          />
        </div>
        {canUpdate && (
          <div className="w400">
            <Inputs.UploadImage
              key={_fileKey}
              theme_upload_image
              acceptType="excel"
              placeholder="เลือกไฟล์ Excel ผังที่นั่ง"
              onChange={_handleSelectFile}
            />
          </div>
        )}
      </div>
      {_seatRows.length > 0 && (
        <div className="info_box">
          พบ {_seatRows.length} แถว ·{" "}
          {Object.entries(zoneSummary)
            .map(([zone, count]) => `โซน ${zone} ${count} แถว`)
            .join(" · ")}
          <div className="w160" style={{ marginTop: 8 }}>
            <Buttons.BgStandard theme_purple label="นำเข้าผังที่นั่ง" onClick={_handleImportSeats} />
          </div>
        </div>
      )}
      {_importErrors.length > 0 && (
        <div className="warn_box">
          แก้ไฟล์แล้วนำเข้าใหม่ (ยังไม่มีการนำเข้า):
          <ul>
            {_importErrors.map((e) => (
              <li key={`${e.line}-${e.message}`}>
                แถว Excel ที่ {e.line}: {e.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="section_title">รูปผังฮอลล์</div>
      <div className="hint">
        ไม่บังคับ ลูกค้าเห็นรูปนี้ในหน้าเลือกที่นั่ง ถ้าวาดกรอบโซนไว้ ลูกค้ากดบนรูปเพื่อเลือกโซนได้ (กดปุ่มโซนด้านล่างรูปได้เหมือนเดิม)
      </div>
      {canUpdate && (
        <div className="inline_row" style={{ marginTop: 10 }}>
          <div className="w400">
            <Inputs.UploadImage
              key={_mapKey}
              theme_upload_image
              acceptType="cover"
              placeholder="เลือกรูปผัง PNG / JPG ไม่เกิน 8 MB"
              onChange={_handleUploadMap}
            />
          </div>
          {config.seat_map_image && (
            <div className="w160">
              <Buttons.BgStandard theme_red label="ลบรูปผัง" onClick={_handleRemoveMap} />
            </div>
          )}
        </div>
      )}
      {config.seat_map_image && (
        <div style={{ marginTop: 10 }}>
          {zones.length === 0 && <div className="hint">นำเข้าผังที่นั่งก่อน จึงจะวาดกรอบโซนได้</div>}
          <Displays.ZoneAreaEditor
            src={IMG_PATH + config.seat_map_image}
            zones={zones}
            areas={_areas}
            onChange={_setAreas}
            disabled={!canUpdate || zones.length === 0}
          />
          {canUpdate && areasChanged && (
            <div className="inline_row" style={{ marginTop: 10 }}>
              <div className="w160">
                <Buttons.BgStandard theme_purple label="บันทึกกรอบโซน" onClick={_handleSaveAreas} />
              </div>
              <div className="w120">
                <Buttons.BgStandard theme_gray label="ยกเลิก" onClick={() => _setAreas(config.zone_areas || [])} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SettingTab;
